/**
 * /admin/daybook — browse imported vouchers (the Day Book).
 * @file src/web/features/admin/pages/daybook.tsx
 *
 * Searchable + type-filtered, paginated voucher table. Click a row to
 * expand its ledger entries (fetched on demand). Amounts + Dr/Cr.
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { toast } from '@bloomneo/uikit';
import { SEO } from '../../../shared/components';
import { AdminPageHeader } from '../components/AdminPageHeader';
import { PageLoading } from '../components/PageLoading';
import { adminFetch, adminFetchJson } from '../lib/admin-api';
import { useCompany } from '../lib/company-context';
import { usePeriod } from '../lib/period-context';
import { PeriodBar } from '../components/PeriodBar';
import { VoucherModal } from '../components/VoucherModal';
import { Plus, Pencil } from 'lucide-react';
import { Pager } from './ledgers';

interface Voucher { id: string; date: string; type: string; number: string | null; narration: string | null; amount: number; source?: string; edited?: boolean }
interface Page { total: number; page: number; pageSize: number; items: Voucher[] }
interface Entry { ledgerName: string; amount: number; isDebit: boolean }

function inr(n: number) {
  return Math.abs(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const TYPE_COLORS: Record<string, string> = {
  Payment: 'bg-danger-50 text-danger-600', Receipt: 'bg-ok-50 text-ok-600',
  Sales: 'bg-highlighter-40 text-inkblack', Purchase: 'bg-warn-50 text-warn-600',
  Journal: 'bg-highlighter-40 text-inkblack', Contra: 'bg-info-50 text-info-600',
};

export default function DayBookPage() {
  const [params] = useSearchParams();
  const { entityId } = useCompany();
  const { from, to } = usePeriod();
  const [q, setQ] = useState('');
  const [type, setType] = useState(params.get('type') ?? '');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<Page | null>(null);
  const [loading, setLoading] = useState(true);
  const [types, setTypes] = useState<{ type: string; count: number }[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [modalV, setModalV] = useState<null | { id: string | null }>(null);
  const [tab, setTab] = useState<'entries' | 'queued'>('entries');
  const [queued, setQueued] = useState<QueuedItem[]>([]);
  const refresh = () => { setRefreshKey((k) => k + 1); setOpen(null); };

  useEffect(() => { setPage(1); }, [entityId, from, to, type]);

  const loadQueued = () => {
    if (!entityId) { setQueued([]); return; }
    adminFetchJson<{ items: QueuedItem[] }>('/api/books/writeback', { query: { entityId } })
      .then((d) => setQueued(d.items ?? [])).catch(() => setQueued([]));
  };
  useEffect(loadQueued, [entityId, refreshKey]);

  useEffect(() => {
    adminFetchJson<{ items: { type: string; count: number }[] }>('/api/tally-import/vouchers/types', { query: { entityId: entityId ?? undefined, from: from || undefined, to: to || undefined } })
      .then((d) => setTypes(d.items)).catch(() => {});
  }, [entityId, from, to]);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    const t = setTimeout(() => {
      adminFetchJson<Page>('/api/tally-import/vouchers', { query: { q, type, page, entityId: entityId ?? undefined, from: from || undefined, to: to || undefined } })
        .then((d) => !cancel && setData(d))
        .finally(() => !cancel && setLoading(false));
    }, q ? 250 : 0);
    return () => { cancel = true; clearTimeout(t); };
  }, [q, type, page, entityId, from, to, refreshKey]);

  const pages = data ? Math.ceil(data.total / data.pageSize) : 1;

  return (
    <>
      <SEO title="Day Book" />
      <AdminPageHeader title="Day Book" breadcrumbs={[{ label: 'Books', href: '/admin/books' }, { label: 'Day Book' }]} />

      <PeriodBar />

      <div className="mb-4 flex flex-wrap gap-1 rounded-xl border border-ink-200 bg-white p-1">
        <TabBtn active={tab === 'entries'} onClick={() => setTab('entries')} label="Entries" />
        <TabBtn
          active={tab === 'queued'}
          onClick={() => setTab('queued')}
          label="Queued for Tally"
          count={queued.length}
        />
      </div>

      {tab === 'queued' ? (
        <QueuedPanel items={queued} entityId={entityId ?? null} onChanged={loadQueued} />
      ) : (
      <>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-full max-w-xs">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
            <input
              value={q}
              onChange={(e) => { setPage(1); setQ(e.target.value); }}
              placeholder="Search number or narration…"
              className="w-full rounded-xl border border-ink-200 bg-white py-2.5 pl-10 pr-4 text-sm shadow-sm focus:border-inkblack focus:outline-none focus:ring-2 focus:ring-highlighter"
            />
          </div>
          <select
            value={type}
            onChange={(e) => { setPage(1); setType(e.target.value); }}
            className="rounded-xl border border-ink-200 bg-white py-2.5 pl-3 pr-8 text-sm shadow-sm focus:border-inkblack focus:outline-none focus:ring-2 focus:ring-highlighter"
          >
            <option value="">All types</option>
            {types.map((t) => <option key={t.type} value={t.type}>{t.type} ({t.count})</option>)}
          </select>
        </div>
        <div className="flex items-center gap-3">
          {data && <span className="shrink-0 text-sm text-ink-500">{data.total.toLocaleString('en-IN')} vouchers</span>}
          <button onClick={() => setModalV({ id: null })} className="inline-flex items-center gap-2 rounded-full bg-inkblack px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:-translate-y-0.5">
            <Plus className="h-4 w-4" /> New voucher
          </button>
        </div>
      </div>

      {loading && !data ? (
        <PageLoading label="Loading Day Book" />
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-ink-200 bg-white">
          <table className="w-full min-w-[44rem] text-sm">
            <thead>
              <tr className="border-b border-ink-100 text-left text-xs uppercase tracking-wide text-ink-400">
                <th className="w-8 px-3 py-3" />
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">No.</th>
                <th className="px-4 py-3 font-medium">Narration</th>
                <th className="px-5 py-3 text-right font-medium">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-50">
              {data?.items.map((v) => (
                <VoucherRow key={v.id} v={v} open={open === v.id} onToggle={() => setOpen(open === v.id ? null : v.id)} onEdit={() => setModalV({ id: v.id })} />
              ))}
              {data && data.items.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-ink-400">No vouchers found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {data && pages > 1 && <Pager page={data.page} pages={pages} onPage={setPage} />}
      </>
      )}

      {modalV && <VoucherModal entityId={entityId} voucherId={modalV.id} onClose={() => setModalV(null)} onSaved={() => { setModalV(null); refresh(); }} />}
    </>
  );
}

function VoucherRow({ v, open, onToggle, onEdit }: { v: Voucher; open: boolean; onToggle: () => void; onEdit: () => void }) {
  const [entries, setEntries] = useState<Entry[] | null>(null);
  useEffect(() => {
    if (open && !entries) {
      adminFetchJson<{ entries: Entry[] }>(`/api/tally-import/vouchers/${v.id}`)
        .then((d) => setEntries(d.entries)).catch(() => setEntries([]));
    }
  }, [open, entries, v.id]);

  return (
    <>
      <tr className="cursor-pointer hover:bg-ink-50/60" onClick={onToggle}>
        <td className="px-3 py-3 text-ink-400">{open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</td>
        <td className="px-4 py-3 tabular-nums text-ink-700">{v.date}</td>
        <td className="px-4 py-3">
          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${TYPE_COLORS[v.type] ?? 'bg-ink-100 text-ink-600'}`}>{v.type}</span>
          {v.source === 'manual' && <span className="ml-1.5 rounded-full bg-highlighter-40 px-1.5 py-0.5 text-[10px] font-medium text-inkblack">manual</span>}
          {v.source !== 'manual' && v.edited && <span className="ml-1.5 rounded-full bg-warn-50 px-1.5 py-0.5 text-[10px] font-medium text-warn-600">edited</span>}
        </td>
        <td className="px-4 py-3 text-ink-500">{v.number ?? '—'}</td>
        <td className="max-w-xs truncate px-4 py-3 text-ink-500">{v.narration || '—'}</td>
        <td className="px-5 py-3 text-right">
          <div className="flex items-center justify-end gap-2">
            <span className="font-medium tabular-nums text-ink-900">{inr(v.amount)}</span>
            <button onClick={(e) => { e.stopPropagation(); onEdit(); }} title="Edit voucher" className="grid size-7 place-items-center rounded-lg text-ink-300 hover:bg-ink-100 hover:text-ink-600"><Pencil className="h-3.5 w-3.5" /></button>
          </div>
        </td>
      </tr>
      {open && (
        <tr className="bg-ink-50/50">
          <td />
          <td colSpan={5} className="px-4 py-3">
            {!entries ? (
              <span className="text-xs text-ink-400">Loading entries…</span>
            ) : (
              <div className="overflow-hidden rounded-lg border border-ink-200 bg-white">
                <table className="w-full text-sm">
                  <tbody className="divide-y divide-ink-50">
                    {entries.map((e, i) => (
                      <tr key={i}>
                        <td className="px-4 py-2 text-ink-700">{e.ledgerName}</td>
                        <td className="px-4 py-2 text-right tabular-nums">
                          <span className={e.isDebit ? 'text-ink-900' : 'text-ink-400'}>{e.isDebit ? inr(e.amount) : ''}</span>
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">
                          <span className={!e.isDebit ? 'text-ink-900' : 'text-ink-400'}>{!e.isDebit ? inr(e.amount) : ''}</span>
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-ink-50 text-xs font-medium text-ink-500">
                      <td className="px-4 py-1.5">Dr / Cr</td>
                      <td className="px-4 py-1.5 text-right tabular-nums">{inr(entries.filter((e) => e.isDebit).reduce((s, e) => s + e.amount, 0))}</td>
                      <td className="px-4 py-1.5 text-right tabular-nums">{inr(entries.filter((e) => !e.isDebit).reduce((s, e) => s + e.amount, 0))}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

interface QueuedItem {
  id: string;
  kind: 'ledger' | 'voucher';
  action: 'create' | 'alter' | 'delete';
  name: string;
  summary: string;
  createdAt: string;
}

function TabBtn({ active, onClick, label, count }: { active: boolean; onClick: () => void; label: string; count?: number }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition ${active ? 'bg-inkblack text-white' : 'text-ink-600 hover:bg-ink-50'}`}
    >
      {label}
      {count != null && count > 0 && (
        <span className={`rounded-full px-1.5 py-0.5 text-[11px] font-semibold ${active ? 'bg-white/20 text-white' : 'bg-warn-100 text-warn-700'}`}>{count}</span>
      )}
    </button>
  );
}

/**
 * Changes made here that are waiting on the client's Tally.
 *
 * A cloud edit or deletion does not reach the client's books until they confirm
 * it in the desktop app, and until now that wait was invisible: a deleted
 * voucher simply vanished from the Day Book with no sign that anything was
 * still outstanding. This is that queue, and it can be withdrawn — cancelling
 * a queued deletion brings the record back.
 */
function QueuedPanel({ items, entityId, onChanged }: { items: QueuedItem[]; entityId: string | null; onChanged: () => void }) {
  const [busy, setBusy] = useState<string | null>(null);

  const cancel = async (it: QueuedItem) => {
    setBusy(it.id);
    try {
      const res = await adminFetch(`/api/books/writeback/${it.id}`, { method: 'DELETE', query: { entityId: entityId ?? undefined } });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'Could not withdraw');
      toast.success(it.action === 'delete' ? 'Withdrawn — the record is back' : 'Withdrawn');
      onChanged();
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusy(null); }
  };

  if (!items.length) {
    return (
      <div className="rounded-2xl border border-dashed border-ink-300 bg-ink-50/50 px-6 py-14 text-center">
        <p className="text-sm font-medium text-ink-700">Nothing waiting on the client</p>
        <p className="mx-auto mt-1 max-w-md text-sm text-ink-500">
          Edits, new entries and deletions made here queue up until the client confirms
          them in the desktop app. Right now there is nothing outstanding.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
      <div className="border-b border-ink-100 bg-ink-50/60 px-6 py-3 text-xs text-ink-600">
        These have not reached the client's Tally yet. They are applied only when the
        client confirms them in the desktop app — withdrawing one here cancels it, and
        a withdrawn deletion restores the record.
      </div>
      <table className="w-full text-sm">
        <thead className="border-b border-ink-100 text-left text-xs uppercase tracking-wide text-ink-500">
          <tr><th className="px-6 py-3 font-medium">Change</th><th className="px-6 py-3 font-medium">Type</th><th className="px-6 py-3 font-medium">Queued</th><th className="px-6 py-3 text-right font-medium">Actions</th></tr>
        </thead>
        <tbody className="divide-y divide-ink-50">
          {items.map((it) => (
            <tr key={it.id} className="hover:bg-ink-50/50">
              <td className="px-6 py-3 text-ink-800">{it.summary}</td>
              <td className="px-6 py-3">
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${it.action === 'delete' ? 'bg-danger-50 text-danger-700' : it.action === 'create' ? 'bg-ok-50 text-ok-700' : 'bg-ink-100 text-ink-600'}`}>
                  {it.action}
                </span>
                <span className="ml-1.5 text-[11px] text-ink-400">{it.kind}</span>
              </td>
              <td className="px-6 py-3 text-xs text-ink-500">{new Date(it.createdAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</td>
              <td className="px-6 py-3 text-right">
                <button
                  onClick={() => cancel(it)}
                  disabled={busy === it.id}
                  className="inline-flex items-center gap-1.5 rounded-full border border-ink-300 bg-white px-3 py-1 text-xs font-semibold text-ink-700 transition hover:bg-ink-50 disabled:opacity-50"
                >
                  {busy === it.id && <Loader2 className="h-3 w-3 animate-spin" />} Withdraw
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
